// src/components/financials/BudgetManager.js
// Updated: 01/25/2026 - Added Totals, Sorting, and Type Safety

import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Grid, TextField, LinearProgress, Card, CardContent, 
  Divider, IconButton, MenuItem, Alert, Paper 
} from '@mui/material';
import { Edit, CheckCircle, Warning, LockClock, CalendarMonth, AccountBalanceWallet } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, getDocs, doc, setDoc, query, where } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const BudgetManager = () => {
  const { userProfile, currentUser } = useAuth();
  const [funds, setFunds] = useState([]);
  const [expenses, setExpenses] = useState({});
  const [budgets, setBudgets] = useState({}); 
  const [budgetMetadata, setBudgetMetadata] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [tempAmount, setTempAmount] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Totals for Summary Header
  const [totals, setTotals] = useState({ budget: 0, spent: 0 });

  const systemYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(systemYear);
  const yearOptions = [systemYear - 1, systemYear, systemYear + 1];

  const churchId = userProfile?.churchId;

  useEffect(() => {
    if (!churchId) return;
    loadData();
  }, [churchId, selectedYear]);

  const loadData = async () => {
    setLoading(true);
    try {
      // 1. Get Funds
      const fundsSnap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'funds'));
      const fundsList = fundsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      // SORT ALPHABETICALLY
      fundsList.sort((a,b) => (a.name || "").localeCompare(b.name || ""));
      setFunds(fundsList);

      // 2. Get Expenses
      const startOfYear = `${selectedYear}-01-01`;
      const endOfYear = `${selectedYear}-12-31`;
      const expRef = collection(db, 'churches', churchId, 'financials', 'data', 'expenses');
      const q = query(
        expRef, 
        where('status', '==', 'approved'), 
        where('date', '>=', startOfYear),
        where('date', '<=', endOfYear)
      );
      const expSnap = await getDocs(q);
      
      const expenseMap = {};
      let totalSpent = 0;

      expSnap.docs.forEach(doc => {
        const data = doc.data();
        const amt = Number(data.amount) || 0; // Ensure Number type
        expenseMap[data.fundId] = (expenseMap[data.fundId] || 0) + amt;
        totalSpent += amt;
      });
      setExpenses(expenseMap);

      // 3. Get Budgets
      const budgetSnap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'budgets'));
      const budgetMap = {};
      const metaMap = {};
      let totalBudget = 0;

      budgetSnap.docs.forEach(doc => {
        const data = doc.data();
        if (data.year === selectedYear) {
          const limit = Number(data.annualLimit) || 0;
          budgetMap[data.fundId] = limit;
          metaMap[data.fundId] = data.updatedBy;
          totalBudget += limit;
        }
      });
      setBudgets(budgetMap);
      setBudgetMetadata(metaMap);
      setTotals({ budget: totalBudget, spent: totalSpent });

    } catch (e) {
      console.error("Error loading budget data:", e);
    }
    setLoading(false);
  };

  const handleSaveBudget = async (fundId) => {
    try {
      const budgetDocId = `${selectedYear}_${fundId}`;
      const ref = doc(db, 'churches', churchId, 'financials', 'data', 'budgets', budgetDocId);
      const userId = currentUser?.uid || 'unknown_admin'; 
      const newLimit = parseFloat(tempAmount) || 0;

      await setDoc(ref, {
        fundId,
        year: selectedYear,
        annualLimit: newLimit,
        updatedBy: userId
      }, { merge: true });

      // Update Local State Optimistically
      const oldLimit = budgets[fundId] || 0;
      setBudgets({ ...budgets, [fundId]: newLimit });
      setBudgetMetadata({ ...budgetMetadata, [fundId]: userId });
      setTotals(prev => ({ ...prev, budget: prev.budget - oldLimit + newLimit }));
      
      setEditingId(null);
    } catch (e) {
      alert("Failed to save: " + e.message);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Fund Utilization Planner
            </Typography>
            <Typography variant="body2" color="textSecondary">
                Set annual spending limits per fund for {selectedYear}.
            </Typography>
        </Box>

        <TextField
          select
          size="small"
          label="Fiscal Year"
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          sx={{ width: 150 }}
          InputProps={{
            startAdornment: <CalendarMonth sx={{ mr: 1, color: 'action.active' }} />,
          }}
        >
          {yearOptions.map(y => (
            <MenuItem key={y} value={y}>{y}</MenuItem>
          ))}
        </TextField>
      </Box>

      {/* SUMMARY HEADER */}
      {funds.length > 0 && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
              <Grid container alignItems="center" spacing={2}>
                  <Grid item>
                      <AccountBalanceWallet color="primary" sx={{ fontSize: 40 }} />
                  </Grid>
                  <Grid item xs>
                      <Typography variant="subtitle2" color="textSecondary">TOTAL BUDGET</Typography>
                      <Typography variant="h5" fontWeight="bold">${totals.budget.toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs>
                      <Typography variant="subtitle2" color="textSecondary">TOTAL SPENT</Typography>
                      <Typography variant="h5" fontWeight="bold">${totals.spent.toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs={12} md={5}>
                       <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption">Utilization</Typography>
                            <Typography variant="caption">{totals.budget > 0 ? ((totals.spent / totals.budget) * 100).toFixed(1) : 0}%</Typography>
                       </Box>
                       <LinearProgress 
                            variant="determinate" 
                            value={totals.budget > 0 ? Math.min((totals.spent / totals.budget) * 100, 100) : 0} 
                            sx={{ height: 10, borderRadius: 5 }}
                            color={totals.spent > totals.budget ? 'error' : 'primary'}
                       />
                  </Grid>
              </Grid>
          </Paper>
      )}

      {/* EMPTY STATE HANDLING */}
      {!loading && funds.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
              <strong>No Funds Found:</strong> Please configure funds in the Setup menu before creating budgets.
          </Alert>
      )}

      <Grid container spacing={3}>
        {funds.map((fund) => {
          const spent = expenses[fund.id] || 0;
          const limit = budgets[fund.id] || 0;
          const hasBudget = limit > 0;
          const percent = hasBudget ? (spent / limit) * 100 : 0;
          const isOver = percent > 100;
          const lastUpdatedBy = budgetMetadata[fund.id];

          return (
            <Grid item xs={12} md={6} lg={4} key={fund.id}>
              <Card variant="outlined" sx={{ borderColor: isOver ? 'error.main' : 'divider', height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6" noWrap sx={{ maxWidth: '60%' }}>{fund.name}</Typography>
                    {editingId === fund.id ? (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField 
                          size="small" type="number" 
                          autoFocus
                          value={tempAmount} onChange={(e) => setTempAmount(e.target.value)}
                          placeholder="$ Limit" sx={{ width: 100 }}
                        />
                        <IconButton size="small" color="primary" onClick={() => handleSaveBudget(fund.id)}>
                          <CheckCircle />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="caption" display="block" align="right">Annual Limit</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <Typography fontWeight="bold" sx={{ mr: 1, color: hasBudget ? 'text.primary' : 'text.secondary' }}>
                            {hasBudget ? `$${limit.toLocaleString()}` : "No Limit"}
                          </Typography>
                          <IconButton size="small" onClick={() => { setEditingId(fund.id); setTempAmount(limit || ''); }}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ my: 1 }} />
                  
                  {/* Progress Bar & Stats */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Spent: <strong>${spent.toLocaleString()}</strong></Typography>
                    <Typography variant="body2" color={isOver ? "error" : "textSecondary"}>
                      {hasBudget ? `${percent.toFixed(1)}%` : ""}
                    </Typography>
                  </Box>

                  {hasBudget ? (
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min(percent, 100)} 
                      color={isOver ? "error" : (percent > 85 ? "warning" : "success")}
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                  ) : (
                    <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', display: 'flex', alignItems: 'center' }}>
                      <LockClock fontSize="inherit" sx={{ mr: 0.5 }} />
                      Set a limit to track progress.
                    </Typography>
                  )}
                  
                  {isOver && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', color: 'error.main' }}>
                      <Warning fontSize="small" sx={{ mr: 1 }} />
                      <Typography variant="caption" fontWeight="bold">Over Budget</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default BudgetManager;